import type Highcharts from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import type { Setup } from "../../../../api/useSetups";
import getFailureSeries from "../../Chart/series/getFailureSeries";
import getNoFailureSeries from "./getNoFailureSeries";
import getSymptomSerie from "../../Chart/series/getSymptomSerie";
import getProcessRemainingSymptomSerie from "../../Chart/series/getRemainingSymptomSerie";
import getInternalGapSerie from "../../Chart/series/getInternalGapSerie";
import getUnexplainedGapSerie from "../../Chart/series/getUnexplainedGapSerie";
import getAnalysisFlagSerie from "../../Chart/series/getAnalysisFlagSerie";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import getManualFailureSeries from "../../Chart/series/getManualFailuresSeries";
import getConfidenceAreaSerie from "../../Chart/series/getConfidenceAreaSerie";
import { useMetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import { useTranslations } from "../../../../api/useTranslations";
import getInvalidQuantityFlagSerie from "../../Chart/series/getInvalidQuantityFlagSerie";
import { useDisplayInvalidPoints } from "../../Analysis/ToggleDisplayInvalidPoints";
import type { FailureValueStatus } from "../../Chart/PointMetadata";
import type { QuantityIndex, Quantity } from "../../../../api/useQuantities";

const emptySet = new Set<FailureValueStatus>();
const useSeries = ({
  project,
  yAxisId,
  setups,
  analyses,
  indexedFailureValues,
  indexedFailureMetadata,
  indexedManualFailureValues,
  qoi,
  includeSuspectedFailure,
  actionAllowed,
  quantityIndex,
  publicationFlagDisplayed,
  displayImpact,
}: {
  project: Project;
  yAxisId: string;
  setups: Setup[];
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedManualFailureValues: IndexedManualFailureValues;
  qoi: Quantity | null;
  includeSuspectedFailure: boolean;
  actionAllowed: boolean;
  quantityIndex: QuantityIndex;
  publicationFlagDisplayed: boolean;
  displayImpact: boolean;
}): (
  | Highcharts.SeriesLineOptions
  | Highcharts.SeriesAreaOptions
  | Highcharts.SeriesArearangeOptions
  | Highcharts.SeriesScatterOptions
)[] => {
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const translations = useTranslations();
  const metrologyOverrides = useMetrologyOverrides(project.id);

  const failuresSeries = React.useMemo(
    () =>
      getFailureSeries({
        type: "line",
        failureSerieType: "OBSERVABLE",
        project,
        yAxisId,
        setups,
        analyses,
        indexedFailureValues,
        indexedFailureMetadata,
        qoi,
        startLegendIndex: 2,
        actionAllowed,
        failureValueStatusFilter: emptySet,
      }),
    [
      actionAllowed,
      analyses,
      project,
      qoi,
      setups,
      yAxisId,
      indexedFailureValues,
      indexedFailureMetadata,
    ],
  );

  const manualFailuresSeries = React.useMemo(
    () =>
      getManualFailureSeries({
        project,
        yAxisId,
        analyses,
        indexedManualFailureValues,
        qoi,
        startLegendIndex: failuresSeries.length + 1,
        actionAllowed: false,
        type: "line",
        replicateInNavigator: true,
      }),
    [analyses, project, qoi, yAxisId, indexedManualFailureValues, failuresSeries],
  );

  const invalidSeries = React.useMemo(
    () =>
      displayInvalidPoints
        ? null
        : getInvalidQuantityFlagSerie({
            analyses,
            quantities: qoi ? [qoi] : [],
            metrologyOverrides,
          }),
    [analyses, qoi, metrologyOverrides, displayInvalidPoints],
  );

  const areaRangeSeries = React.useMemo(() => {
    const series: Highcharts.SeriesArearangeOptions[] = [];
    if (qoi) {
      series.push(
        getInternalGapSerie({
          yAxisId,
          analyses,
          setups,
          quantity: qoi,
          legendIndex: 0,
          route: "diagnosis",
          metrologyOverrides,
          translations,
        }),
        getUnexplainedGapSerie({
          yAxisId,
          indexedFailureValues,
          indexedFailureMetadata,
          indexedManualFailureValues,
          analyses,
          setups,
          quantity: qoi,
          includeSuspectedFailure,
          legendIndex: 1,
          metrologyOverrides,
          translations,
        }),
      );
    } else if (displayImpact) {
      series.push(
        getSymptomSerie({
          yAxisId,
          analyses,
          setups,
          legendIndex: 0,
          metrologyOverrides,
          quantityIndex,
        }),
        getProcessRemainingSymptomSerie({
          yAxisId,
          analyses,
          indexedFailureValues,
          indexedFailureMetadata,
          setups,
          includeSuspectedFailure,
          legendIndex: 1,
          metrologyOverrides,
          quantityIndex,
        }),
        getConfidenceAreaSerie({
          indexedFailureValues,
          yAxisId,
          analyses,
          legendIndex: 2,
        }),
      );
    }
    return series;
  }, [
    analyses,
    displayImpact,
    includeSuspectedFailure,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    metrologyOverrides,
    qoi,
    quantityIndex,
    setups,
    translations,
    yAxisId,
  ]);

  const noFailureSerie = React.useMemo(
    () =>
      getNoFailureSeries({
        yAxisId,
        analyses,
        project,
        indexedFailureValues,
        indexedFailureMetadata,
        legendIndex: failuresSeries.length + manualFailuresSeries.length + 1,
      }),
    [
      analyses,
      failuresSeries.length,
      indexedFailureMetadata,
      indexedFailureValues,
      manualFailuresSeries.length,
      project,
      yAxisId,
    ],
  );

  const flagSerie = React.useMemo(
    () =>
      getAnalysisFlagSerie({
        project,
        setups,
        analyses,
        quantityIndex,
        chartType: "SYMPTOMS",
        publicationFlagDisplayed,
      }),
    [analyses, project, setups, publicationFlagDisplayed, quantityIndex],
  );

  return React.useMemo(
    () =>
      [
        ...areaRangeSeries,
        ...failuresSeries,
        ...manualFailuresSeries,
        noFailureSerie,
        invalidSeries,
        flagSerie,
      ].filter(s => s !== null),
    [
      areaRangeSeries,
      failuresSeries,
      flagSerie,
      invalidSeries,
      manualFailuresSeries,
      noFailureSerie,
    ],
  );
};

export default useSeries;
